<template>
  <div v-if="chart_insights && chart_insights.length" :class="collapseClass">
    <!-- Header row with title and sort control -->
    <div class="insight-feed-header">
      <div style="width: 100%">
        <button @click="closeInsights()" class="find-insights-close-button">
          <img
            :src="'/assets/insight_generate.png'"
            :alt="`Generate insights icon`"
            class="find-insights-icon"
          />
          <div class="find-insights-label">
            <VisibleText> Insights </VisibleText>
          </div>
        </button>
      </div>

      <div class="insight-sort-bar" v-if="showInsights">
        <label for="sortSelect" class="sr-only">Sort insights</label>
        <select id="sortSelect" v-model="sortOrder" class="sort-select">
          <option value="">No sort</option>
          <option value="desc">Highest first</option>
          <option value="asc">Lowest first</option>
        </select>
      </div>
    </div>

    <!-- Filter buttons row -->
    <div class="insight-filter-bar" v-if="showInsights">
      <div v-if="orderedTypes.length">Show only:</div>
      <button
        v-for="t in orderedTypes"
        :key="t"
        :aria-pressed="activeTypes.includes(t)"
        @click="toggleType(t)"
        class="filter-button"
        :class="{ active: activeTypes.includes(t) }"
      >
        <img :src="getIconForType(t)" :alt="`${t} icon`" class="filter-icon" />
        {{ t }}
      </button>
    </div>

    <!-- Insights feed -->
    <div class="insight-feed-scroll" v-if="showInsights">
      <div class="insight-card" v-for="(insight, index) in sortedInsights" :key="index">
        <p>
          <InsertMarkdown :text="insight.description"></InsertMarkdown>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import InsertMarkdown from '@/components/InsertMarkdown/InsertMarkdown.vue';

export default {
  name: 'InsightsFeed',
  components: { InsertMarkdown },
  props: {
    chart_insights: {
      type: Array,
      default: () => [],
    },
    isAbsolute: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      activeTypes: [],
      sortOrder: 'desc', // 'asc' or 'desc',
      showInsights: true,
    };
  },
  mounted() {
    if (this.showInsights) {
      this.$emit('openStatus', true);
    }
  },
  computed: {
    collapseClass() {
      let collapsedClass = 'insight-feed-container';
      if (!this.showInsights) {
        collapsedClass = 'insight-feed-container_small';
        if (this.isAbsolute) {
          collapsedClass = collapsedClass + ' ' + 'insight-feed-container_absolute';
        }
      }

      return collapsedClass;
    },
    // enforce fixed order for filter buttons
    orderedTypes() {
      const order = [
        'high score',
        'high volume',
        'low score',
        'low volume',
        'priority driver',
        'top driver',
        'priority theme',
        'top theme',
        'negative sentiment',
        'positive sentiment',
        'outlier',
        'cluster',
        'trend upward',
        'trend downward',
        'stable trend',
        'no trend',
        'spike',
        'dip',
        'other',
      ];

      const allTypes = this.chart_insights.flatMap((i) => i.type || []);

      // TODO to include other or not? Or only to include it when more than one label exists?
      // Check for any insight with missing or unrecognized types
      // const hasOther = this.chart_insights.some(insight => {
      //   const types = insight.type || [];
      //   return types.length === 0 || types.some(t => !order.includes(t));
      // });

      const matchedTypes = order.filter((t) => allTypes.includes(t));

      // if (hasOther && !matchedTypes.includes('other')) {
      //   matchedTypes.push('other');
      // }

      return matchedTypes;
    },
    filteredInsights() {
      if (!this.activeTypes.length) return this.chart_insights;
      return this.chart_insights.filter((insight) =>
        insight.type?.some((t) => this.activeTypes.includes(t)),
      );
    },
    sortedInsights() {
      if (!this.sortOrder) return this.filteredInsights;

      const getValue = (insight) =>
        insight.means ?? insight.valid_size ?? insight.coefficient ?? insight.coverage ?? null;

      const sortable = this.filteredInsights.filter((i) => getValue(i) !== null);

      return sortable.sort((a, b) => {
        const valA = getValue(a);
        const valB = getValue(b);
        if (this.sortOrder === 'asc') return valA - valB;
        if (this.sortOrder === 'desc') return valB - valA;
        return 0;
      });
    },
  },
  methods: {
    toggleType(type) {
      if (this.activeTypes.includes(type)) {
        this.activeTypes = this.activeTypes.filter((t) => t !== type);
      } else {
        this.activeTypes.push(type);
      }
    },
    getIconForType(type) {
      const iconMap = {
        'high score': '/assets/insight_high_score.png',
        'high volume': '/assets/insight_high_volume.png',
        'low score': '/assets/insight_low_score.png',
        'low volume': '/assets/insight_low_volume.png',
        'priority driver': '/assets/insight_priority_driver.png',
        'top driver': '/assets/insight_top_driver.png',
        'priority theme': '/assets/insight_priority_theme.png',
        'top theme': '/assets/insight_top_theme.png',
        'negative sentiment': '/assets/insight_negative_sentiment.png',
        'positive sentiment': '/assets/insight_positive_sentiment.png',
        outlier: '/assets/insight_outlier.png',
        cluster: '/assets/insight_cluster.png',
        'trend upward': '/assets/insight_trend_upward.png',
        'trend downward': '/assets/insight_trend_downward.png',
        'stable trend': '/assets/insight_trend_stable.png',
        'no trend': '/assets/insight_trend_none.png',
        spike: '/assets/insight_spike.png',
        dip: '/assets/insight_dip.png',
      };
      return iconMap[type] || '/assets/insight_other.png';
    },
    closeInsights() {
      this.showInsights = !this.showInsights;
    },
  },
  watch: {
    showInsights(newVal) {
      this.$emit('openStatus', newVal);
    },
  },
};
</script>
